import { useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { updateCandidateResponse } from "../store/slices/miscSlice";
import host from "../global";
import { candidateAuthHeader } from "../utils/candidateAuth";

const ASSEMBLY_TOKEN_URL = `${host}/api/assembly/token`;
const ASSEMBLYAI_WS_URL = "wss://streaming.assemblyai.com/v3/ws";
const SAMPLE_RATE = 16000;

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000; // 1s, doubles each retry (exponential backoff)
const FLUSH_TIMEOUT_MS = 500;

export function useTranscriptionSocket() {
    const dispatch = useDispatch();
    const { interview_id: interviewId } = useParams();

    const wsRef = useRef(null);
    const isReadyRef = useRef(false);
    const queueRef = useRef([]);
    const retryCountRef = useRef(0);
    const retryTimerRef = useRef(null);
    const shouldReconnectRef = useRef(true); // set to false on intentional close
    const connectRef = useRef(() => {}); // holds latest connect(), set below — lets scheduleRetry call it without a circular dep
    const pendingFlushResolveRef = useRef(null); // resolver for flushFinalTranscript(), set while awaiting a forced turn
    const assemblyTtsEnabled = useSelector((state)=>state.miscSlice.assemblyTtsEnabled);
    const assemblySpeechModel = useSelector((state)=>state.miscSlice.assemblySpeechModel);
    const assemblyLanguageCodes = useSelector((state)=>state.miscSlice.assemblyLanguageCodes);

    const scheduleRetry = useCallback(() => {
        if (!shouldReconnectRef.current) {
            console.log("[WS] Skipping retry — shouldReconnect is false");
            return;
        }

        if (retryCountRef.current >= MAX_RETRIES) {
            console.error(`[WS] Max retries (${MAX_RETRIES}) reached. Giving up.`);
            return;
        }

        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        const delay = BASE_DELAY_MS * Math.pow(2, retryCountRef.current);
        retryCountRef.current += 1;

        console.log(
            `[WS] Retrying — attempt ${retryCountRef.current}/${MAX_RETRIES} in ${delay}ms`
        );

        retryTimerRef.current = setTimeout(() => {
            console.log(`[WS] Retry attempt ${retryCountRef.current}/${MAX_RETRIES} firing now`);
            connectRef.current();
        }, delay);
    }, []);

    const connect = useCallback(async () => {
        // AssemblyAI off for this interview (feature disabled, or language it can't stream) —
        // don't fetch a token or open the socket; STT falls back to Whisper.
        if (!assemblyTtsEnabled) {
            console.log("[WS] AssemblyAI disabled for this interview — using Whisper fallback.");
            return;
        }

        // Already open or mid-handshake — skip
        if (
            wsRef.current &&
            (wsRef.current.readyState === WebSocket.OPEN ||
                wsRef.current.readyState === WebSocket.CONNECTING)
        ) {
            console.log("[WS] connect() skipped — already open/connecting");
            return;
        }

        console.log("[WS] Requesting AssemblyAI token...");
        let token;
        try {
            const { data } = await axios.get(ASSEMBLY_TOKEN_URL, {
                params: { expires_in_seconds: 600, interview_id: interviewId },
                headers: candidateAuthHeader(interviewId),
            });
            token = data.token;
        } catch (err) {
            console.error("[WS] Token request failed:", err);
            scheduleRetry();
            return;
        }

        if (!token) {
            console.error("[WS] Token response missing 'token' field:", ASSEMBLY_TOKEN_URL);
            scheduleRetry();
            return;
        }

        console.log("[WS] Token received. Opening WebSocket to AssemblyAI...");
        const speechModel = assemblySpeechModel || "universal-streaming-english";
        let url = `${ASSEMBLYAI_WS_URL}?sample_rate=${SAMPLE_RATE}&encoding=pcm_s16le&format_turns=true&speech_model=${speechModel}`;
        if (assemblyLanguageCodes && assemblyLanguageCodes.length) {
            url += `&language_codes=${encodeURIComponent(JSON.stringify(assemblyLanguageCodes))}`;
        }
        url += `&token=${token}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log("[WS] Connected — interview_id:", interviewId);
            isReadyRef.current = true;
            retryCountRef.current = 0; // reset retries on successful connect

            if (queueRef.current.length > 0) {
                console.log(`[WS] Flushing ${queueRef.current.length} queued chunks`);
                queueRef.current.forEach((chunk) => ws.send(chunk));
                queueRef.current = [];
            }
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === "Begin") {
                    console.log("[WS] AssemblyAI session began:", data);
                } else if (data.type === "Turn") {
                    if (data.transcript && data.end_of_turn) {
                        console.log("[WS] Final transcript:", data.transcript);
                        dispatch(updateCandidateResponse(data.transcript));

                        if (pendingFlushResolveRef.current) {
                            console.log("[WS] Flush resolved by final Turn");
                            pendingFlushResolveRef.current();
                            pendingFlushResolveRef.current = null;
                        }
                    }
                } else if (data.type === "Error") {
                    console.error("[WS] AssemblyAI error message:", data);
                } else if (data.type === "Termination") {
                    console.log("[WS] AssemblyAI session terminated:", data);
                }
            } catch (err) {
                console.error("[WS] Failed to parse message:", err);
            }
        };

        ws.onerror = (err) => {
            console.error("[WS] Connection error:", err);
            isReadyRef.current = false;
        };

        ws.onclose = (event) => {
            console.log("[WS] Closed — code:", event.code, "reason:", event.reason || "(none)");
            isReadyRef.current = false;

            if (!shouldReconnectRef.current) {
                console.log("[WS] Not reconnecting — intentional close");
                return;
            }

            console.log("[WS] Unexpected close — will attempt to reconnect");
            scheduleRetry();
        };
    }, [interviewId, dispatch, scheduleRetry, assemblyTtsEnabled, assemblySpeechModel, assemblyLanguageCodes]);

    connectRef.current = connect;

    const initConnection = useCallback(() => {
        console.log("[WS] initConnection() called");
        shouldReconnectRef.current = true;
        retryCountRef.current = 0;
        connect();
    }, [connect]);

    const sendChunk = useCallback((pcmArrayBuffer) => {
        if(!assemblyTtsEnabled) return;
        const ws = wsRef.current;

        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(pcmArrayBuffer);
        } else {
            queueRef.current.push(pcmArrayBuffer);
        }
    }, []);

    const flushFinalTranscript = useCallback(() => {
        const ws = wsRef.current;

        if (!ws || ws.readyState !== WebSocket.OPEN) {
            console.log("[WS] flushFinalTranscript skipped — socket not open");
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            const startedAt = performance.now();
            pendingFlushResolveRef.current = () => {
                console.log(`[WS] ForceEndpoint resolved in ${(performance.now() - startedAt).toFixed(0)}ms`);
                resolve();
            };

            console.log("[WS] Sending ForceEndpoint to flush pending turn");
            ws.send(JSON.stringify({ type: "ForceEndpoint" }));

            setTimeout(() => {
                if (pendingFlushResolveRef.current) {
                    console.warn(`[WS] ForceEndpoint timed out after ${(performance.now() - startedAt).toFixed(0)}ms — proceeding anyway`);
                    pendingFlushResolveRef.current = null;
                    resolve();
                }
            }, FLUSH_TIMEOUT_MS);
        });
    }, []);

    const closeConnection = useCallback(() => {
        console.log("[WS] closeConnection() called — closing intentionally");
        shouldReconnectRef.current = false; // prevent auto-reconnect
        isReadyRef.current = false;

        if (retryTimerRef.current) {
            console.log("[WS] Cancelling pending retry timer");
            clearTimeout(retryTimerRef.current); // cancel any pending retry
            retryTimerRef.current = null;
        }

        setTimeout(() => {
            queueRef.current = [];
            if (wsRef.current) {
                if (wsRef.current.readyState === WebSocket.OPEN) {
                    console.log("[WS] Sending Terminate message");
                    wsRef.current.send(JSON.stringify({ type: "Terminate" }));
                }
                wsRef.current.close();
                wsRef.current = null;
                console.log("[WS] Socket closed and cleared");
            }
    }, 1500);
    }, []);

    useEffect(() => {
        return () => closeConnection();
    }, [closeConnection]);

    return { initConnection, sendChunk, closeConnection, flushFinalTranscript };
}